import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { SUPPORTED_LANGUAGES } from "../i18n";

/**
 * Language selector used inside the settings drawer. Persists choice to:
 *  1. localStorage (wta_lang) — handled by i18next-browser-languagedetector
 *  2. backend user.preferred_language — best-effort, so it follows across devices
 */
export default function LanguageSwitcher({ compact = false }) {
    const { i18n } = useTranslation();
    const { user, setUser } = useAuth();

    const onChange = async (code) => {
        await i18n.changeLanguage(code);
        if (user) {
            try {
                await api.patch("/users/me/language", { language: code });
                setUser?.({ ...user, preferred_language: code });
            } catch { /* silent: UI already changed locally */ }
        }
    };

    if (compact) {
        return (
            <select
                data-testid="language-switcher-compact"
                value={i18n.language?.split("-")[0] || "en"}
                onChange={(e) => onChange(e.target.value)}
                className="bg-wta-surface2 border border-wta-border rounded-sm px-2 py-1 text-xs"
            >
                {SUPPORTED_LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>{l.flag} {l.native}</option>
                ))}
            </select>
        );
    }

    return (
        <div data-testid="language-switcher" className="grid grid-cols-1 gap-1">
            {SUPPORTED_LANGUAGES.map((l) => {
                const active = (i18n.language || "en").split("-")[0] === l.code;
                return (
                    <button
                        key={l.code}
                        data-testid={`lang-${l.code}`}
                        onClick={() => onChange(l.code)}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm transition ${
                            active
                                ? "bg-wta-primary/15 border border-wta-primary/50 text-wta-primary"
                                : "border border-transparent text-white/70 hover:bg-white/5"
                        }`}
                    >
                        <span className="text-base">{l.flag}</span>
                        <span className="flex-1 text-left">{l.native}</span>
                        {active && <span className="text-[10px] tracking-widest uppercase">✓</span>}
                    </button>
                );
            })}
        </div>
    );
}
